import { useEffect, useState } from 'react';
import { Box, Divider, Switch } from '@chakra-ui/react';
import { Button } from '@mercator-gcp/grout';
import { Spinner } from 'components/elements/Spinner';
import { Text } from 'components/elements/Text';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { AccountSettingsModule } from 'components/widgets/AccountSettings/layout/AccountSettingsModule';
import { AccountSettingsSection } from 'components/widgets/AccountSettings/layout/AccountSettingsSection';
import { useGroupConfigurations } from 'hooks/useGroupConfig';
import {
  useNotificationSettings,
  useToggleProjectGroupEnabled,
} from 'hooks/useNotificationSettings';
import { ProjectGroups } from 'pages/AccountSettings/ProjectGroups';
import { useUser } from 'state/auth';

export const Notifications = () => {
  const { user } = useUser();
  const { data: groups, isLoading: loading } = useGroupConfigurations({
    userId: String(user?.id),
  });
  const { result: settings } = useNotificationSettings(String(user?.profile_id));
  const [isSwitchedOn, setIsSwitchedOn] = useState<boolean>(
    settings?.data.project_group_enabled || false,
  );
  const { execute: toggleProjectGroupEnabled } = useToggleProjectGroupEnabled(
    String(user?.profile_id),
  );

  // This state variable holds the currently selected view.
  // 0 for first view (<Index />), 1 for second view (<ProjectGroups />).
  const [currentView, setCurrentView] = useState(0);

  const handleSwitchChange = () => {
    const newSwitchState = !isSwitchedOn;
    setIsSwitchedOn((prevIsSwitchedOn) => !prevIsSwitchedOn);
    toggleProjectGroupEnabled(newSwitchState);
  };

  const togglePage = () => {
    if (currentView === 0) {
      setCurrentView(1);
    } else {
      setCurrentView(0);
    }
  };

  useEffect(() => {
    if (!settings?.data.project_group_enabled) {
      // If the group is not enabled, exit early.
      return;
    }
    setIsSwitchedOn(settings?.data.project_group_enabled);
  }, [settings?.data.project_group_enabled]);

  const linkStyles = {
    color: 'brand.army-green',
    display: isSwitchedOn ? 'flex' : 'none',
  };
  const copyStyles = {
    '.chakra-link': {
      color: 'brand.army-green',
    },
    gap: '8px',
  };

  const switchStyles = {
    size: 'lg',
    colorScheme: 'green',
  };

  const switchText = {
    mr: 'eight',
  };

  const typeHeading = {
    fontSize: '16px',
    fontWeight: '600',
  };

  const bodyText = {
    fontSize: '13px',
  };

  const subTitleContainer = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    color: 'brand.text.quiet',
  };

  const loadingContainer = {
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    mt: 'eight',
  };

  const Index = (
    <>
      <Box sx={subTitleContainer}>
        <Text>
          Manage your settings to customize how you are notified about projects.
        </Text>
      </Box>
      <Divider my="4" />
      {loading ? (
        <FlexRow sx={loadingContainer}>
          <Spinner />
        </FlexRow>
      ) : (
        <FlexRow>
          <FlexRow.Left>
            <FlexColumn sx={copyStyles}>
              <Text sx={typeHeading}>Project Groups</Text>
              <Text sx={bodyText}>
                New projects tailored to your set criteria and filters
              </Text>
              <Button
                variant="unstyled"
                sx={{ ...linkStyles, ...bodyText }}
                onClick={togglePage}
              >
                Edit Individual Project Group settings
              </Button>
            </FlexColumn>
          </FlexRow.Left>
          <FlexRow.Right>
            <Text sx={switchText}>{isSwitchedOn ? 'On' : 'Off'}</Text>
            <Switch
              {...switchStyles}
              onChange={handleSwitchChange}
              isChecked={isSwitchedOn}
            />
          </FlexRow.Right>
        </FlexRow>
      )}
    </>
  );

  return (
    <AccountSettingsSection title="Notifications">
      {currentView === 0 && <AccountSettingsModule>{Index}</AccountSettingsModule>}
      {currentView === 1 && <ProjectGroups groups={groups} setPage={setCurrentView} />}
    </AccountSettingsSection>
  );
};
